import { Suspense, useMemo } from 'react';
import { Outlet } from 'react-router';

import { FallbackComponent } from '../components';
import { useUserSessionData } from './selectors';
import { UnauthorizedPage } from './unauthorized';

interface AuthenticatedProps {
  children?: React.ReactNode;
  permissions: string[];
  fallback?: React.ReactElement | null;
}

/**
 * Authentication wrapper component
 * - Checks if user has required permissions before rendering children
 * - Can be used for both route and component-level protection
 *
 * @param {Object} props - Component props
 * @param {string[]} props.permissions - Required permissions ('*' for any authenticated user)
 * @param {React.ReactNode} [props.children] - Content to protect
 * @param {React.ReactElement} [props.fallback] - Fallback UI when unauthorized (default: null)
 */
export const Authenticated: React.FC<AuthenticatedProps> = ({
  permissions = [],
  children,
  fallback = null,
}) => {
  const userSession = useUserSessionData();

  /**
   * Checks if user has required permissions
   * - Returns true if user has any of the required permissions
   * - Wildcard ('*') allows any authenticated user
   */
  const hasPermission = useMemo(() => {
    if (!userSession?.permissions) return false;
    return permissions.some(
      (permission) =>
        userSession?.permissions.includes(permission) || permission === '*'
    );
  }, [permissions, userSession?.permissions]);

  // Return fallback if user lacks permissions
  if (!hasPermission) return fallback;

  // Render protected content
  return <>{children}</>;
};

/**
 * Route-level authentication wrapper
 * - Specialized version of Authenticated for routes
 * - Includes Suspense boundary for lazy loading
 * - Uses Outlet for nested routes
 * - Shows unauthorized page by default when access denied
 */
export const AuthRoute: React.FC<AuthenticatedProps> = (props) => {
  return (
    <Authenticated fallback={<UnauthorizedPage />} {...props}>
      {/* Suspense boundary for code-split routes */}
      <Suspense fallback={<FallbackComponent requesting selfOverlay />}>
        {/* Render child routes */}
        <Outlet />
      </Suspense>
    </Authenticated>
  );
};
